import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { HelmetProvider } from 'react-helmet-async';

// Pages
import LandingPage from '@/pages/LandingPage';
import ChiSiamo from '@/pages/ChiSiamo';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import Onboarding from '@/pages/Onboarding';
import Dashboard from '@/pages/Dashboard';
import Events from '@/pages/Events';
import EventCreate from '@/pages/EventCreate';
import EventDetail from '@/pages/EventDetail';
import EventInvitations from '@/pages/EventInvitations';
import EventPublic from '@/pages/EventPublic';
import OrderConfirmation from '@/pages/OrderConfirmation';
import Scanner from '@/pages/Scanner';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import AdminPanel from '@/pages/AdminPanel';
import AdminPartners from '@/pages/AdminPartners';
import TicketAcceptance from '@/pages/TicketAcceptance';
import TicketPhotoUpload from '@/pages/TicketPhotoUpload';
import ScannerAuthorize from '@/pages/ScannerAuthorize';
import ExamplesLanding from '@/pages/ExamplesLanding';
import Settings from '@/pages/Settings';
import EventStream from '@/pages/EventStream';
import HelpCenter from '@/pages/HelpCenter';
import NewsletterManager from '@/pages/NewsletterManager';
import AdminDashboard from '@/pages/AdminDashboard';
import PushNotificationsSettings from '@/pages/PushNotificationsSettings';
import JitsiStorageSettings from '@/pages/JitsiStorageSettings';
import AppLauncher from '@/pages/AppLauncher';
import MyTickets from '@/pages/MyTickets';
import ScannerMobile from '@/pages/ScannerMobile';
import ScannerActivation from '@/pages/ScannerActivation';
import PaymentSuccess from '@/pages/PaymentSuccess';

// Components
import ChatWidget from '@/components/ChatWidget';
import PWAInstall from '@/components/PWAInstall';
import { LanguageProvider } from '@/contexts/LanguageContext';

const ProtectedRoute = ({ children }) => {
  const { user, tenant, loading } = useAuth();

  if (loading) {
    return <div role="status" data-testid="session-loading" className="flex min-h-screen items-center justify-center text-sm text-gray-600">Caricamento…</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Redirect to onboarding if not completed
  if (tenant && !tenant.onboarding_completed && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" />;
  }
  
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
          {/* Public Landing Page */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/chi-siamo" element={<ChiSiamo />} />
          
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/examples" element={<ExamplesLanding />} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/event/:eventId" element={<EventPublic />} />
          <Route path="/events/:eventId/stream" element={<EventStream />} />
          <Route path="/order/:orderId/confirmation" element={<OrderConfirmation />} />
          
          {/* Ticket public routes */}
          <Route path="/ticket/:ticketId/accept" element={<TicketAcceptance />} />
          <Route path="/ticket/:ticketId/upload-photo" element={<TicketPhotoUpload />} />
          
          {/* Scanner authorization */}
          <Route path="/scanner/authorize/:qrData" element={<ScannerAuthorize />} />
          
          <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
          <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/partners" element={<ProtectedRoute><AdminPartners /></ProtectedRoute>} />
          <Route path="/newsletter" element={<ProtectedRoute><NewsletterManager /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/settings/notifications" element={<ProtectedRoute><PushNotificationsSettings /></ProtectedRoute>} />
          <Route path="/settings/jitsi-storage" element={<ProtectedRoute><JitsiStorageSettings /></ProtectedRoute>} />
          <Route path="/launcher" element={<ProtectedRoute><AppLauncher /></ProtectedRoute>} />
          <Route path="/my-tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
          <Route path="/scanner" element={<ProtectedRoute><ScannerMobile /></ProtectedRoute>} />
          <Route path="/scanner/activate" element={<ScannerActivation />} />
          <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
          <Route path="/events/new" element={<ProtectedRoute><EventCreate /></ProtectedRoute>} />
          <Route path="/events/:eventId/manage" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
          <Route path="/events/:eventId/invitations" element={<ProtectedRoute><EventInvitations /></ProtectedRoute>} />
          <Route path="/scanner" element={<ProtectedRoute><Scanner /></ProtectedRoute>} />
        </Routes>
        <Toaster position="top-right" />
        <ChatWidget />
        <PWAInstall />
      </BrowserRouter>
    </AuthProvider>
  </LanguageProvider>
</HelmetProvider>
  );
}

export default App;
